import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { isAxiosError } from 'axios';
import { useFetchCallback } from '@services/services';
import { MSG_DELETE_ORDER, MSG_DELETE_ORDER_ERROR, MSG_DELETE_ORDER_OK, MSG_LIST_ERROR } from '@pages/Orders/messages';
import { ERROR_CODE, ORDER_PAYMENT_MAP, ORDER_STATUS_ACTIONS, ORDER_STATUS_MAP } from '@utils/constants';
import { DeliveryRiderInfo, Order, OrderDetail } from '@interfaces/OrderTypes';
import toastValidator from '@components/interface/ToastValidator';
import { getCurrentStatus, getNextStatus, getStatusIndex } from '@utils/getOrderStatus';
import { useOrderActions, useOrdersFromStore } from '@store/actions/orders';
import { useOrderWithDetailsActions, useOrdersWithDetailsFromStore } from '@store/actions/ordersWithDetails';

import { useGetCommerce } from '@pages/Commerce/hooks/commerce';

type HandleCloseSelfServiceOrderProps = {
  orderList: Order[];
  tableUUID: string;
  force?: boolean;
  paymentMethod: keyof typeof ORDER_PAYMENT_MAP;
};

export const useDetailedOrders = () => {
  const orders = useOrdersWithDetailsFromStore();
  const { addOrderWithDetails } = useOrderWithDetailsActions();
  const { isFetching, call: fetchOrderDetail } = useFetchCallback<OrderDetail>('get', '/v1/pedido');

  const getOrder = async (id: string) => {
    const existingOrder = orders.find((item) => item.pedido.id === id);
    if (existingOrder) {
      return existingOrder;
    }

    try {
      const order = await fetchOrderDetail({ path: id });
      const mappedOrder: OrderDetail = {
        ...order,
        pedido: {
          ...order.pedido,
          metodo_pago: order.pedido.metodo_pago as OrderDetail['pedido']['metodo_pago'],
        },
      };
      if ((mappedOrder && mappedOrder.pedido.estatus !== 'pending') || mappedOrder?.pedido?.self_service?.order_uuid) {
        addOrderWithDetails(mappedOrder);
      }
      return mappedOrder;
    } catch (e) {
      toast.error('No se encontró el pedido');
      throw new Error('No se encontró el pedido');
    }
  };
  const forceRefetchOrder = async (id: string) => {
    try {
      const order = await fetchOrderDetail({ path: id });
      const mappedOrder: OrderDetail = {
        ...order,
        pedido: {
          ...order.pedido,
          metodo_pago: order.pedido.metodo_pago as OrderDetail['pedido']['metodo_pago'],
        },
      };
      addOrderWithDetails(mappedOrder);
      return mappedOrder;
    } catch (e) {
      toast.error('No se encontró el pedido');
      throw new Error('No se encontró el pedido');
    }
  };

  return { isFetching, getOrder, forceRefetchOrder, orders };
};

export const useGetOrdersWithStatus = () => {
  const orders = useOrdersFromStore();
  const [isClosingTable, setIsClosingTable] = useState(false);
  const { initOrders, addOrder, updateOrder } = useOrderActions();
  const { updateOrderWithDetails } = useOrderWithDetailsActions();
  const ordersWithDetails = useOrdersWithDetailsFromStore();
  const { commerce } = useGetCommerce();
  const [isUpdatingOrderStatus, setIsUpdatingOrderStatus] = useState(false);
  const [isUpdatingPaymentMethod, setIsUpdatingPaymentMethod] = useState(false);
  const { call: closeTable } = useFetchCallback<{ table: string; payment_method: string }, { table: string }>(
    'post',
    '/orders/v2/close',
    'v2',
  );
  const { isFetching, call: fetchOrders } = useFetchCallback<null, { count: number; rows: Order[] }>(
    'get',
    '/v1/pedido',
  );
  const { call: closeTableForced } = useFetchCallback<{ status: 'a' }, { uuid: string }>('patch', '/commerce', 'v2');

  const { call: handleUpdate } = useFetchCallback<{
    action: string;
    status?: keyof typeof ORDER_STATUS_MAP;
    paymentMethod?: string;
  }>('put', '/v1/pedido');

  useEffect(() => {
    if (!orders.length && commerce?.id) {
      fetchOrders({ path: '?orders_in_process=true' })
        .then((response) => {
          if (response && response.rows) initOrders([...response.rows]);
        })
        .catch(() => {
          toast.error(MSG_LIST_ERROR);
        });
    }
  }, [commerce]);

  const setStatusToOrder = async (
    order: Order,
    newStatus: keyof typeof ORDER_STATUS_MAP,
    { force = false }: { force?: boolean } = {},
  ) => {
    const orderExistsInStore = orders.find((o) => o.id === order.id);

    if (orderExistsInStore) {
      if (!force) {
        const currentIndex = getStatusIndex(orderExistsInStore);
        const newIndex = getStatusIndex({ ...orderExistsInStore, estatus: newStatus } as Order);
        if (newIndex !== -1 && currentIndex !== -1 && newIndex < currentIndex) {
          return;
        }
      }
      updateOrder({ ...orderExistsInStore, estatus: newStatus });
    } else {
      addOrder({ ...order, estatus: newStatus });
    }

    const detailedOrder = ordersWithDetails.find((item) => item.pedido.id === order.id);
    if (detailedOrder) {
      updateOrderWithDetails({
        productos: detailedOrder.productos,
        pedido: { ...detailedOrder.pedido, estatus: newStatus },
      });
    }
  };

  const setStatusAndPaymentMethodToOrder = async (
    order: Order,
    newStatus: keyof typeof ORDER_STATUS_MAP,
    paymentMethod: keyof typeof ORDER_PAYMENT_MAP,
  ) => {
    updateOrder({ ...order, estatus: newStatus, metodo_pago: paymentMethod });

    const detailedOrder = ordersWithDetails.find((item) => item.pedido.id === order.id);
    if (detailedOrder) {
      updateOrderWithDetails({
        productos: detailedOrder.productos,
        pedido: {
          ...detailedOrder.pedido,
          estatus: newStatus,
          metodo_pago: paymentMethod,
        },
      });
    }
  };

  const handleUpdateStatus = async (
    order: Order,
    statusAction: ORDER_STATUS_ACTIONS,
    immediate?: boolean,
    cook_time?: number,
  ) => {
    const newStatus = (): keyof typeof ORDER_STATUS_MAP => {
      if (statusAction === 'reject') return ORDER_STATUS_MAP.rejected.key;
      if (statusAction === 'cancel') return ORDER_STATUS_MAP.canceled.key;
      if (statusAction === 'next') return getNextStatus(order).key;

      return getCurrentStatus(order).key;
    };

    const prevStatus = order.estatus || ORDER_STATUS_MAP.pending.key;
    try {
      setIsUpdatingOrderStatus(true);
      const newOrderStatus = newStatus();

      if (immediate) {
        setStatusToOrder(order, newOrderStatus);
      }

      await handleUpdate({
        path: order.id,
        data: {
          action: 'changeStatus',
          status: newOrderStatus,
          ...(cook_time !== undefined && { cook_time }),
        },
      });

      if (!immediate) {
        setStatusToOrder(order, newOrderStatus);
      }
    } catch (err) {
      if (isAxiosError(err) && err.response?.data?.errorCode === ERROR_CODE.INVALID_STATUS_TRANSITION) {
        const currentStatus = err.response.data.data?.currentStatus;
        if (currentStatus) {
          setStatusToOrder(order, currentStatus, { force: true });
        }
        return;
      }

      setStatusToOrder(order, prevStatus, { force: true });
      toast.error('Error al actualizar el estatus del pedido');
    } finally {
      setIsUpdatingOrderStatus(false);
    }
  };

  const handleUpdatePaymentMethod = async (order: Order, paymentMethod: keyof typeof ORDER_PAYMENT_MAP) => {
    try {
      setIsUpdatingPaymentMethod(true);

      await handleUpdate({
        path: order.id,
        data: {
          action: 'changePaymentMethod',
          paymentMethod: ORDER_PAYMENT_MAP[paymentMethod].actualKey || ORDER_PAYMENT_MAP[paymentMethod].key,
        },
      });

      setStatusAndPaymentMethodToOrder(order, order.estatus as keyof typeof ORDER_STATUS_MAP, paymentMethod);
      toast.success('Método de pago actualizado correctamente');
    } catch (err) {
      toast.error('Error al actualizar el método de pago');
    } finally {
      setIsUpdatingPaymentMethod(false);
    }
  };

  const handleCloseSelfServiceOrder = async ({
    orderList,
    tableUUID,
    force = false,
    paymentMethod,
  }: HandleCloseSelfServiceOrderProps) => {
    try {
      setIsClosingTable(true);

      const ordersFromThisTable = orderList.filter((order) => order.self_service?.table_uuid === tableUUID);

      await Promise.all(
        ordersFromThisTable.map((order) =>
          handleUpdate({
            path: order.id,
            data: {
              action: 'changeStatus',
              status: ORDER_STATUS_MAP.delivered.key,
            },
          }),
        ),
      );

      if (!force) {
        const payedWith = ORDER_PAYMENT_MAP[paymentMethod].actualKey;
        const result = await closeTable({
          data: { table: tableUUID, payment_method: payedWith },
        });

        if (!result.table) {
          throw new Error('Error al cerrar la mesa');
        }
      } else {
        const result = await closeTableForced({
          data: {
            status: 'a',
          },
          path: `${commerce?.id}/tables/${tableUUID}`,
        });

        if (!result.uuid) {
          throw new Error('Error al cerrar la mesa');
        }
      }

      ordersFromThisTable.forEach((order) => {
        setStatusAndPaymentMethodToOrder(order, ORDER_STATUS_MAP.delivered.key, paymentMethod);
      });

      return true;
    } catch {
      throw new Error('Error');
    } finally {
      setIsClosingTable(false);
    }
  };

  const setAssignedRider = (order: Order, rider: DeliveryRiderInfo) => {
    updateOrder({ ...order, delivery_driver_info: rider });

    const detailedOrder = ordersWithDetails.find((item) => item.pedido.id === order.id);
    if (detailedOrder) {
      updateOrderWithDetails({
        productos: detailedOrder.productos,
        pedido: { ...detailedOrder.pedido, delivery_driver_info: rider },
      });
    }
  };

  return {
    isFetching,
    orders,
    setStatusToOrder,
    handleAddOrder: addOrder,
    handleUpdateStatus,
    handleUpdateOrder: updateOrder,
    setAssignedRider,
    handleCloseSelfServiceOrder,
    isClosingTable,
    isUpdatingOrderStatus,
    isUpdatingPaymentMethod,
    handleUpdatePaymentMethod,
  };
};

export const useOrderDelete = () => {
  const { deleteOrder } = useOrderActions();
  const { call: handleDelete, isFetching } = useFetchCallback('delete', '/v1/pedido');

  const handleOrderDelete = (orderId: string) => {
    return new Promise((result) => {
      toastValidator({
        message: MSG_DELETE_ORDER,
        onValidate: async () => {
          try {
            await handleDelete({ path: orderId });
            deleteOrder({ id: orderId });
            toast.success(MSG_DELETE_ORDER_OK);
            result(true);
          } catch (err) {
            toast.error(MSG_DELETE_ORDER_ERROR);
            result(false);
          }
        },
      });
    });
  };

  return { isDeleting: isFetching, handleOrderDelete };
};

export const useDeliveryRider = () => {
  const { call: handleRequestAdvance, isFetching: isFetchingAdvance } = useFetchCallback(
    'post',
    '/v1/delivery/request',
  );
  const { call: handleRequestNew, isFetching: isFetchingNew } = useFetchCallback<{ id_pedido: string }>(
    'post',
    '/v1/delivery/order',
  );
  const { call: handleCancel, isFetching: isCancelling } = useFetchCallback('post', '/v1/delivery/request-cancel');
  const { updateOrderWithDetails } = useOrderWithDetailsActions();

  const requestNewDeliveryRider = async (order: OrderDetail) => {
    try {
      await handleRequestNew({ data: { id_pedido: order.pedido.id } });

      updateOrderWithDetails({
        productos: order.productos,
        pedido: { ...order.pedido, has_requested_rider: true, delivery_status: 'pending' },
      });
      toast.success('Se ha enviado la solicitud al repartidor');
    } catch (err) {
      toast.error('Error al enviar la solicitud');
    }
  };

  const requestAnticipateDeliveryRider = async (order: OrderDetail) => {
    try {
      await handleRequestAdvance({ path: order.pedido.id });

      updateOrderWithDetails({ productos: order.productos, pedido: { ...order.pedido, has_requested_rider: true } });
      toast.success('Se ha enviado la solicitud al repartidor');
    } catch (err) {
      toast.error('Error al enviar la solicitud');
    }
  };

  const cancelDeliveryRider = async (order: OrderDetail) => {
    try {
      await handleCancel({ path: order.pedido.id });

      updateOrderWithDetails({
        productos: order.productos,
        pedido: { ...order.pedido, has_requested_rider: false, delivery_status: 'canceled' },
      });
      toast.success('Se ha cancelado la solicitud al repartidor');
    } catch (err) {
      toast.error('Error al cancelar el repartidor');
    }
  };

  return {
    requestAnticipateDeliveryRider,
    cancelDeliveryRider,
    requestNewDeliveryRider,
    isRequestingNewRider: isFetchingNew,
    isAnticipatingRider: isFetchingAdvance,
    isCancellingRider: isCancelling,
  };
};
